import {useState, useEffect, useMemo, useRef} from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import * as yup from 'yup';
import { ErrorMessage, Formik } from 'formik';
import Button from '../../../common/Button';
import Input from '../../../common/simple/input';
import { newGraySelectStyles } from '../../../common/shared/select.styled';
import arrowInCircle from '../../../images/newDesign/arrow_in_white_circle.svg';
import bank from '../../../images/newDesign/bank.svg';
import rolledArrow from "../../../images/newDesign/rolledArrow.svg";
import {Signature} from './BankSignature';
import PaymentFlowContainer from '../PaymentFlowContainer';
import { StyledBankPage } from './BankPage.styled';
import {useHistory} from "react-router-dom";
import {constants} from "../../../common/constants/constants";
import {useDispatch, useSelector} from "react-redux";
import { resetTenant, setBankCharge, setChargeDate, setPaymentPlan } from "../../../store/tenant/slice";
import {NotificationManager} from "react-notifications";
import Time from "../../../common/utils/time";
import {hideLoader, showLoader} from "../../../store/common/slice";
import ServerData from "../../../common/serverData";
import CacheData from "../../../common/utils/cacheData";
import NoticeModal from './NoticeModal';
import { getBuildingAddress } from '../../../common/shared/statelessMethods';

export function BankPage({ match }) {
    const { t } = useTranslation();
    const { buildingID } = match.params;
    const dispatch = useDispatch();
    const history = useHistory();
    const application = useRef();

    const tenantDetails = useSelector(state => state.tenant.details);
    const buildingAddress = useSelector(state => state.tenant.buildingAddress);
    const createPaymentPlanPayload = useSelector(state => state.tenant.createPaymentPlanPayload);
    const disabledInsurancePage = useSelector(state => state.tenant.disableInsurancePage ?? false);


    const [showNoticeModal, setShowNoticeModal] = useState(false);

    const bankOptions = useMemo(() => {
        // TODO: support for mexico buildings
        // const codes = currency.toUpper === 'MXN' ? constants.bankCodesInMexico : constants.bankWithPhysicalBranchesCodesInIsrael;
        // const country = currency.toUpper === 'MXN' ? 'mexico' : 'israel';
        const codes = constants.bankWithPhysicalBranchesCodesInIsrael;
        const country = 'israel';
        return codes.map(value => ({ value, label: `${value} - ${t(`common.banks.${country}.${value}`)}` }));
    }, []);

    const validationSchema = yup.object().shape({
        bankCode: yup.object().required(t('titles.forms.mandatory_field')),
        branchNumber: yup.string().matches(/^\d+$/, t('titles.forms.number_field')).min(1).max(3).required(t('titles.forms.mandatory_field')),
        accountNumber: yup.string().matches(/^\d+$/, t('titles.forms.number_field')).min(6).max(9).required(t('titles.forms.mandatory_field')),
        ownerId: yup.string().required(t('titles.forms.mandatory_field')),
        signature: yup.string().required(t('titles.forms.mandatory_field')).nullable()
    });

    useEffect(() => {
        application.current.contentWindow.addEventListener('message', handleIframeMessage);
        // NOTE: no need removeEventListener as iframe will be removed from DOM
        // return () => application.current.contentWindow.removeEventListener('message', handleIframeMessage);

        const today = Time.getCurrentDay();
        if (today > 16) setShowNoticeModal(true)
    }, []);

    function handleFormSubmit(values, helpers) {
        const bankChargeData = {
            ownerId: values.ownerId,
            bankName: values.bankCode.label,
            bankCode: Number(values.bankCode.value),
            accountNumber: Number(values.accountNumber),
            branchNumber: Number(values.branchNumber),
            signature: values.signature,
            date: new Date().toJSON(),
        };
        dispatch(setBankCharge(bankChargeData));
        // NOTE: send all data to the iframe, it will generate pdf-file and send it us back
        application.current.contentWindow.postMessage({
            applicationData: true,
            tenantDetails,
            bankChargeData,
            buildingAddress,
        });
    }

    async function handleIframeMessage(event) {
        if (event.data?.bankChargeApplication) {
            const { imageBase64, imageName, bankChargeData } = event.data;
            await createPaymentPlan(imageBase64, imageName, bankChargeData);
        } else if (event.data?.iframeError) {
            NotificationManager.error(event.data.error.message);
        }
    }

    function checkErrors(errors) {
        if (Object.keys(errors).length > 0) {
            NotificationManager.error(t('payments.please_fill'));
        }
    }

    async function createPaymentPlan(imageBase64, imageName, bankChargeData) {
        const payload = {
            ...createPaymentPlanPayload,
            bankChargeData,
            imageBase64,
            imageName,
        };

        try {
            dispatch(showLoader());
            const client = new ServerData();
            const { paymentResult, imageResult } = await client.tenants.payments.bankCharge(buildingID, tenantDetails.apartmentNumber, payload);
            const cartID = paymentResult.cartID;
            if (!cartID) {
                // TODO: use error code and translate it after?
                throw new Error(t('payments.payment_plan_was_not_created'));
            }

            CacheData.cache(`cartID`, cartID);
            NotificationManager.success(t('auto_charge.success'), '', 5000);
            if (disabledInsurancePage){
                dispatch(resetTenant());
                history.push(`/${constants.pages.orderStatusPage}/${cartID}`);
            } else{
                history.push(`/${constants.pages.insuranceOffer}/${buildingID}/${tenantDetails.apartmentNumber}/${cartID}`);
            }
        } catch (e) {
            NotificationManager.error(t([`errors.tenant.payments.${e.response.data.type}`, 'auto_charge.failed']));
        } finally {
            dispatch(hideLoader())
        }
    }

    const onNoticeModalHide = () => setShowNoticeModal(false);

    return (
        <PaymentFlowContainer title={t('payments.payment_to')} subheader={t('auto_charge.title')}>

            <StyledBankPage>
                <img className='bank-img' src={bank} />
                <div className='credit-card-and-actions-block'>
                    <Formik
                        initialValues={{
                            bankCode: '',
                            branchNumber: '',
                            accountNumber: '',
                            ownerId: '',
                            signature: null,
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values, helpers) => handleFormSubmit(values, helpers)}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                            setFieldTouched,
                            setTouched,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit}>
                                    <div className='inputs-wrapper'>
                                        <div className='full-width'>
                                            <label className='label' htmlFor="bankCode">{t('offline_payments.bank_code_title')}</label>
                                            <Select
                                                styles={newGraySelectStyles}
                                                options={bankOptions}
                                                name='bankCode'
                                                onChange={type => setFieldValue('bankCode', type)}
                                                onBlur={() => setFieldTouched('bankCode', true)}
                                                value={values.bankCode}
                                                placeholder={t('payments.select_placeholder')}
                                                isSearchable={false}
                                            />
                                            <ErrorMessage name="bankCode">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>

                                        <div className='full-width'>
                                            <Input
                                                name='branchNumber'
                                                label={t('offline_payments.bank_branch_title')}
                                                value={values.branchNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                inputMode='numeric'
                                            />
                                            <ErrorMessage name="branchNumber">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                    </div>

                                    <div className='inputs-wrapper'>
                                        <div>
                                            <Input
                                                name='accountNumber'
                                                label={t('offline_payments.bank_account_number_title')}
                                                value={values.accountNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                inputMode='numeric'
                                            />
                                            <ErrorMessage name="accountNumber">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>

                                        <div>
                                            <Input
                                                name='ownerId'
                                                label={t('auto_charge.ownerId')}
                                                value={values.ownerId}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                inputMode='numeric'
                                            />
                                            <ErrorMessage name="ownerId">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        </div>
                                    </div>

                                    <div className='full-width'>
                                        <label className='label' htmlFor="bankCode">{t('payments.digital_signature')}</label>
                                        <Signature
                                            className='signature'
                                            name='signature'
                                            value={values.signature}
                                            onChange={sig => setFieldValue('signature', sig)}
                                            onBlur={() => setFieldTouched('signature', true)}
                                        />
                                        <ErrorMessage name="signature">{msg => <div className="error">{msg}</div>}</ErrorMessage>
                                        <div className="clear-arrow">
                                            <button type="button" onClick={() => setFieldValue('signature', null)}>
                                                <img src={rolledArrow}/>
                                            </button>
                                        </div>
                                    </div>

                                    <Button type="submit" onClick={() => checkErrors(errors)}>
                                        {t('payments.makePayment')} <img className='arrow' src={arrowInCircle} />
                                    </Button>
                                </form>
                            )}}
                        </Formik>
                </div>
                <div className="hidden-box">
                    {/* HINT: remove .hidden-box to view pdf-iframe content, it may be helpful for debugging */}
                    <iframe src={`/${constants.pages.bankPageApplication}/${buildingID}`} ref={application} title="Bank charge application" width="1024" height="2000"></iframe>
                </div>
            </StyledBankPage>

            <NoticeModal showModal={showNoticeModal} onHide={onNoticeModalHide} />

        </PaymentFlowContainer>
    )
}
